/** @jsxImportSource react */
import clsxm from '@common/lib/clsxm';
import FieldAdapter from '@react/form/components/FieldAdapter';
import { required } from '@react/form/helpers/validation';
import { getEventPropsForOOB } from '@react/gwizzy/components/OutOfBudgetWizard/helpers';
import {
  CONVERSION_FIELDS as fields,
  CONVERSION_STEPS_MAP as steps,
} from '@react/gwizzy/constants';
import { eventNames, useEvents } from '@react/services/events';
import { Button } from '@react/system';
import { useWizarForm } from '@react/wizards/context/wizardContext';
import FinalFormWizard from '@react/wizards/v2';
import { STARTING_RATES } from '@sly/core/constants/filters';
import { useMemo } from 'react';
import { Field, FormRenderProps } from 'react-final-form';

const OOB_LOCAL_STORAGE_KEY = 'outOfBudget';

const WizardPage = ({ Component, children, ...rest }: any) =>
  Component ? <Component {...rest}>{children}</Component> : children;

const NextButton = ({
  submitText,
  values,
  submitting,
  validating,
  invalid,
  valid,
  onNextStep,
  eventProps = {},
  isLocalStorageEnabled,
}: {
  submitText?: string;
  values: Record<string, any>;
  submitting: boolean;
  validating: boolean;
  invalid: boolean;
  valid: boolean;
  onNextStep: (values: Record<string, any>) => void;
  eventProps?: Record<string, any>;
  isLocalStorageEnabled?: boolean;
}) => {
  const { onNext, isLastPage } = useWizarForm();

  const buttonText = submitText || isLastPage ? 'Submit' : 'Next';

  const handleNext = () => {
    if (isLocalStorageEnabled) {
      localStorage.setItem(OOB_LOCAL_STORAGE_KEY, JSON.stringify(values));
    }
    onNextStep(values);
    onNext(values);
  };

  return (
    <Button
      disabled={submitting || validating || invalid || !valid}
      className='w-full'
      onClick={handleNext}
      eventProps={getEventPropsForOOB({
        ...eventProps,
        values,
      })}
    >
      {buttonText}
    </Button>
  );
};

interface PageComponentProps extends FormRenderProps {
  question?: string;
  submitText?: string;
  className?: string;
  eventProps?: Record<string, any>;
  children: React.ReactNode;
  isLocalStorageEnabled?: boolean;
}

const PageComponent = ({
  question,
  className,
  submitText,
  children,
  eventProps = {},
  isLocalStorageEnabled,
  ...props
}: PageComponentProps) => {
  const { values, submitting, validating, invalid, valid } = props;

  const { events } = useEvents();

  const onNextStep = (values: Record<string, any>) => {
    events.track(
      eventNames.WizardStepCompleted,
      getEventPropsForOOB({
        ...eventProps,
        values,
      })
    );
  };

  return (
    <div className={clsxm('py-12', className)}>
      {question && <h2 className='font-t-m pb-4'>{question}</h2>}
      {children}
      <NextButton
        values={values}
        submitting={submitting}
        validating={validating}
        invalid={invalid}
        valid={valid}
        submitText={submitText}
        onNextStep={onNextStep}
        eventProps={eventProps}
        isLocalStorageEnabled={isLocalStorageEnabled}
      />
    </div>
  );
};

const WizardPageWithNavigation = ({
  name,
  children,
  ...props
}: {
  name: string;
  question?: string;
  className?: string;
  submitText?: string;
  eventProps?: Record<string, any>;
  children: React.ReactNode;
  isLocalStorageEnabled?: boolean;
}) => {
  return (
    <WizardPage name={name} {...props} Component={PageComponent}>
      {children}
    </WizardPage>
  );
};

export default function OutOfBudgetWizard({
  onSubmit,
  initialOptions,
  isLocalStorageEnabled = true,
  eventProps = {},
}: {
  onSubmit: (values: Record<string, any>) => void;
  initialOptions: Record<string, any>;
  isLocalStorageEnabled?: boolean;
  eventProps?: Record<string, any>;
}) {
  const formInitialValues = useMemo(() => {
    const localState =
      isLocalStorageEnabled && typeof window !== 'undefined'
        ? localStorage.getItem(OOB_LOCAL_STORAGE_KEY)
        : '';

    const valueFromLocalStorage = localState ? JSON.parse(localState) : {};

    return initialOptions
      ? {
          adl: initialOptions?.adl,
          budgetRange: initialOptions?.budgetRange,
          ...valueFromLocalStorage,
          location: {
            searchParams: {
              state: initialOptions?.state,
              city: initialOptions?.city,
            },
            displayText: initialOptions?.locationString,
          },
        }
      : {};
  }, [initialOptions, isLocalStorageEnabled]);

  const address = initialOptions?.locationString || '';

  return (
    <FinalFormWizard
      initialValues={formInitialValues}
      localStorageKey=''
      isDefaultSubmitDisabled={false}
      isProgressBarVisible={true}
      initialStep={0}
      asyncActionHandlers={null}
      onSubmit={onSubmit}
    >
      <WizardPageWithNavigation
        name={steps.userLocation}
        question='Where are you looking for senior living options?'
        eventProps={{
          step: 'outOfBudgetLocation',
          ...eventProps,
        }}
        isLocalStorageEnabled={isLocalStorageEnabled}
      >
        <Field
          name='location'
          type='locationSearch'
          validate={required}
          isUsingDefaultCurrentLocationHandler={false}
          types={['administrative_area_level_3', 'locality', 'postal_code']}
          placeholder='Enter your city or zip code'
          className='min-h-[300px]'
          component={FieldAdapter}
          address={address}
        />
      </WizardPageWithNavigation>
      <WizardPageWithNavigation
        name={steps.careLevel}
        question='Do you need assistance with any of the following?'
        eventProps={{
          step: 'outOfBudgetCare',
          ...eventProps,
        }}
        isLocalStorageEnabled={isLocalStorageEnabled}
      >
        <Field
          name={fields.adl.name}
          type='boxChoice'
          validate={required}
          options={fields.adl.options}
          component={FieldAdapter}
        />
      </WizardPageWithNavigation>

      <WizardPageWithNavigation
        name={steps.budgetRange}
        question='What is the total cost of care you believe you can afford?'
        submitText='Show me my options'
        eventProps={{
          step: 'outOfBudgetBudget',
          ...eventProps,
        }}
        isLocalStorageEnabled={isLocalStorageEnabled}
      >
        <Field
          name={fields.budgetRange.name}
          type='boxChoice'
          validate={required}
          options={Object.values(STARTING_RATES)}
          component={FieldAdapter}
          className='md:[&_.input-wrapper]:grid md:[&_.input-wrapper]:grid-cols-2 md:[&_.input-wrapper]:gap-2'
        />
      </WizardPageWithNavigation>
    </FinalFormWizard>
  );
}
